import { CareerTrack } from './careerTrack';
import { AcademicEnrollmentType } from './profile';
import { ColorToken } from '../designSystem/colors/colors';

export type AcademicMajorWithRigor = Readonly<{
  id: number;
  name: string;
  category: string;
  rigor_analytical_complexity: number;
  rigor_competitiveness_attrition: number;
  rigor_performance_standards: number;
  rigor_technical_depth: number;
  rigor_workload: number;
  specialization_alignment: number;
  specialization_demand_security: number;
}>;

export type TrackAssessmentCategorySlug =
  | 'academic-foundations'
  | 'real-world-experience'
  | 'mindsets-and-skills'
  | 'community-of-success'
  | 'none';

export type TrackAssessmentCategory = Readonly<{
  id: number;
  slug: TrackAssessmentCategorySlug;
  name: string;
  shortname: string;
  description: string;
}>;

export type TrackAssessmentStandard = Readonly<{
  id: number;
  tenure: 'High School' | 'Post High School';
  level: CriteriaLevel | null;
  dimension_name: string;
  definition: string | null;
  standard_text: string;
  category: TrackAssessmentCategory;
  careerTrack: CareerTrack;
}>;

export type AcademicGrade = Readonly<{
  id: number;
  name: string;
  value: string;
}>;

export type ProfileStrength = Readonly<{
  overallUnlockStatus: UnlockStatus | null;
  categoriesOnTrack: number;
  categories: CategoryStrength[];
}>;

export const ACADEMIC_TERMS = ['Fall', 'Winter', 'Spring', 'Summer'] as const;

export type AcademicTerm = (typeof ACADEMIC_TERMS)[number];

/** High school programs always start in the fall, so the term is never asked for one. */
export const HIGH_SCHOOL_START_TERM: AcademicTerm = 'Fall';

export type TrackAssessmentResult = Readonly<{
  id: number;
  uuid?: string;
  media_url?: string;
  user_id: number;
  enrollment_type_level: AcademicEnrollmentType;
  academic_grade_id?: number;
  academicGrade?: AcademicGrade;
  academicTerm?: AcademicTerm;
  career_track_id: number;
  gpa: number;
  act_score?: number;
  sat_score?: number;
  academic_rigor_id?: number;
  academicRigor?: {
    id: number;
    name: string;
  };
  school_name?: string;
  academicMajors?: AcademicMajorWithRigor[];
  summary?: string;
  profile_image_url?: string;
  resume_media_id?: number;
  transcript_media_id?: number;
  career_user_response_set_id?: string;
  created_at: string;
  updated_at: string;
  careerTrack?: CareerTrack | null;
  trackAssessmentUnlocks?: TrackAssessmentUnlock[];
  dimensionScores?: TrackAssessmentDimScore[];
  college?: {
    selectivity?: number;
  };
  gpa_thresholds?: GpaThresholds;
  test_score_thresholds?: TestScoreThresholds;
  profileStrength?: ProfileStrength;
}>;

export type TrackAssessmentUnlock = Readonly<{
  id: number;
  category: TrackAssessmentCategory;
  strengths: string[];
  opportunities: string[];
  trackAssessmentResult: Partial<Pick<TrackAssessmentResult, 'uuid' | 'id'>>;
  category_id?: number;
  category_slug?: string;
  strengthKeywords?: string[];
  opportunityKeywords?: string[];
}>;

export type Standard = Readonly<{
  id: number;
  tenure: 'High School' | 'Post High School';
  level: CriteriaLevel | null;
  dimension_name: string;
  definition: string | null;
  standard_text: string;
  category: TrackAssessmentCategory;
  careerTrack: CareerTrack | null;
}>;

/** The two shapes a standards table can take: by career track, or by attainment level. */
export type StandardsVariant =
  | Readonly<{ kind: 'by-track'; standards: Standard[] }>
  | Readonly<{ kind: 'by-level'; criteria: TrackAssessmentDimCriteria[]; gradeLabel: string }>;

export type GpaThresholds = Readonly<{
  education_level: 'High School' | 'College';
  academic_rigor: { id: number; name: string } | null;
  college_selectivity: number | null;
  gpa_thresholds: Record<string, number>;
}>;

export type TestScoreThresholds = Readonly<{
  sat: Record<string, { floor: number; ceiling: number | null }>;
  act: Record<string, { floor: number; ceiling: number | null }>;
}>;

export type DimensionScoreLevelName = 'unmet' | 'minimum' | 'advanced';

export const DIMENSION_SCORE_COLOR_TOKENS: Record<DimensionScoreLevelName, ColorToken> = {
  unmet: 'Brand/50',
  minimum: 'Brand/300',
  advanced: 'Brand/500',
};

export const CRITERIA_LEVEL_SCORES: Record<CriteriaLevel, DimensionScoreLevelName> = {
  Minimum: 'minimum',
  Advanced: 'advanced',
};

export type DimensionScoreLevel = Readonly<{
  id: number;
  name: DimensionScoreLevelName;
  display_order: number;
}>;

export type TrackAssessmentDimension = Readonly<{
  id: number;
  name: string;
  display_order: number;
  category_id: number;
  category: TrackAssessmentCategory;
}>;

export const CRITERIA_LEVELS = ['Minimum', 'Advanced'] as const;

export type CriteriaLevel = (typeof CRITERIA_LEVELS)[number];

export type TrackAssessmentDimCriteria = Readonly<{
  id: number;
  dimension_id: number;
  academic_grade_id: number;
  level: CriteriaLevel;
  criteria_text: string;
  criteria_text_short?: string;
  dimension?: TrackAssessmentDimension;
  academicGrade?: {
    id: number;
    value: string;
  };
}>;

export type TrackAssessmentDimScore = Readonly<{
  id: number;
  track_assessment_result_id: number;
  dimension_id: number;
  score_level_id: number;
  justification?: string | null;
  dimension: TrackAssessmentDimension;
  scoreLevel: DimensionScoreLevel;
  overrideScoreLevel?: DimensionScoreLevel | null;
}>;

export type UnlockStatus = 'not_yet' | 'developing' | 'on_track';

export const UNLOCK_STATUSES: readonly UnlockStatus[] = ['not_yet', 'developing', 'on_track'];

export type CategoryStrength = Readonly<{
  category_id: number;
  category_name: string;
  category_slug: string;
  unlock_status: UnlockStatus;
  dimensions_meeting_threshold: number;
  total_dimensions: number;
}>;

export type ProfileStrengthResponse = Readonly<{
  success: boolean;
  error?: string;
  warning?: string;
  message?: string;
  trackAssessmentResultId?: number;
  blueprint?: {
    tier: string;
  } | null;
  overallUnlockStatus: UnlockStatus | null;
  categoriesOnTrack: number;
  categories: CategoryStrength[];
}>;
